import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Landmark, GraduationCap, Stethoscope, Building2 } from 'lucide-react'

const clientCategories = [
  {
    icon: Landmark,
    title: 'Government Departments',
    clients: [
      'Maharashtra State Government - Multiple Departments',
      'Pune Municipal Corporation',
      'Nashik Municipal Corporation',
      'Aurangabad Municipal Corporation',
      'Maharashtra Public Distribution System',
      'State Health Department',
      'Department of Education, Maharashtra',
      'State Police Department',
    ],
  },
  {
    icon: GraduationCap,
    title: 'Educational Institutions',
    clients: [
      'Savitribai Phule Pune University',
      'Government Polytechnic Colleges (50+)',
      'Zilla Parishad Schools (1000+)',
      'State Institute of Engineering & Technology',
      'Industrial Training Institutes (200+)',
      'Medical Colleges',
      'Business Schools',
    ],
  },
  {
    icon: Stethoscope,
    title: 'Healthcare Organizations',
    clients: [
      'Civil Hospitals (10+)',
      'District Hospitals',
      'Primary Health Centers (500+)',
      'State Disease Control Society',
      'Public Health Laboratories',
      'Medical Research Centers',
    ],
  },
  {
    icon: Building2,
    title: 'Corporate & PSUs',
    clients: [
      'Maharashtra State Electricity Board',
      'State Transport Corporation',
      'Industrial Development Corporations',
      'Housing Boards',
      'Water Supply & Sanitation Boards',
    ],
  },
]

const Clients = () => {
  return (
    <>
      <Helmet>
        <title>Our Clients | Eonixa Dynamics</title>
        <meta name="description" content="Trusted by Maharashtra Government, Universities, Hospitals, and Public Sector Organizations" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Our Clients</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Trusted by leading government departments, educational institutions,
                healthcare organizations, and public sector undertakings.
              </p>
            </div>
          </FadeIn>

          {/* Stats */}
          <FadeIn delay={0.2}>
            <div className="grid gap-8 sm:grid-cols-4 mb-16">
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">50+</p>
                <p className="mt-2 text-gray-600">Government Departments</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">50+</p>
                <p className="mt-2 text-gray-600">Educational Institutions</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">100+</p>
                <p className="mt-2 text-gray-600">Healthcare Facilities</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">50+</p>
                <p className="mt-2 text-gray-600">Corporate & PSUs</p>
              </div>
            </div>
          </FadeIn>

          {/* Client Categories */}
          <div className="grid gap-12">
            {clientCategories.map((category, index) => {
              const Icon = category.icon
              return (
                <FadeIn key={category.title} delay={index * 0.1}>
                  <div className="rounded-2xl bg-white p-8 shadow-soft">
                    <div className="mb-6 flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {category.title}
                      </h2>
                    </div>
                    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {category.clients.map((client) => (
                        <li
                          key={client}
                          className="flex items-start gap-2 text-gray-700"
                        >
                          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" />
                          <span className="text-sm">{client}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.6} className="mt-16 text-center">
            <div className="rounded-2xl bg-gradient-to-r from-primary-900 to-primary-700 p-12 text-white">
              <h2 className="mb-4 text-2xl font-bold">Join Our Growing Family</h2>
              <p className="mx-auto max-w-2xl text-gray-200 mb-8">
                Experience the Eonixa advantage with our proven track record of
                successful implementations and dedicated support.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-900 transition-all hover:bg-gray-100"
              >
                Get Started
              </a>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default Clients
